import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  History,
  Building2,
  Calendar,
  Target,
  ChevronRight,
  Plus
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CatalystHistory = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRuns();
  }, []);

  const loadRuns = async () => {
    try {
      const response = await axios.get(`${API}/catalyst/runs`);
      setRuns(response.data.runs || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (!score) return "bg-gray-500";
    if (score >= 80) return "bg-green-500";
    if (score >= 65) return "bg-amber-500";
    if (score >= 50) return "bg-orange-500";
    return "bg-red-500";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/catalyst')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <History className="w-6 h-6" />
                  Catalyst History
                </h1>
                <p className="text-blue-200">Your previous assessments</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/catalyst/wizard')}
              className="bg-amber-500 hover:bg-amber-600 text-blue-900"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Assessment
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-blue-900 mx-auto mb-4" />
            <p className="text-gray-600">Loading history...</p>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadRuns}>Try Again</Button>
            </CardContent>
          </Card>
        ) : runs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Assessments Yet</h2>
              <p className="text-gray-600 mb-6">
                Run your first Catalyst assessment to see your business optimization program.
              </p>
              <Button
                onClick={() => navigate('/catalyst/wizard')}
                className="bg-blue-900 hover:bg-blue-800"
              >
                Start Your First Assessment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {runs.map((run) => (
              <Card
                key={run.id}
                className="hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => navigate(`/catalyst/results/${run.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-900" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{run.company_name}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span>{run.industry || 'N/A'}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(run.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {run.scores?.CBIS !== undefined && (
                        <div className="text-center">
                          <div className={`w-14 h-14 ${getScoreColor(run.scores.CBIS)} rounded-xl flex items-center justify-center`}>
                            <span className="text-white font-bold text-lg">{run.scores.CBIS}</span>
                          </div>
                          <span className="text-xs text-gray-500">CBIS</span>
                        </div>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {runs.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 mb-4">
              Showing {runs.length} assessment{runs.length !== 1 ? 's' : ''}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/catalyst/wizard')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Run New Assessment
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalystHistory;
